import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { ToastTypes, withToast } from "../../contexts/toastr.context";
import useCache from "../../hooks/use-cache.hook";
import useLang from "../../hooks/use-lang.hook";
import { ActionModal } from "../../_components/action-modal.component";
import { TabBody } from "../../_components/atoms/tab-body.component";
import { TabPills } from "../../_components/atoms/tab-pills.component";
import { Avatar } from "../../_components/avatar.component";
import UpdateRoleModal from "../../_components/community/update-role-modal.component";
import IconMenu2 from "../../_components/icon-menu-2.component";
import Pagination from "../../_components/pagination.component";
import { TextInput } from "../../_components/text-input.component";
import UIcon from "../../_components/uicon-component";
import { history } from "../../_config";
import { CommunityService, EmailService } from "../../_service";
import { setCommunity } from "../../_store/_actions/community.actions";
import { getDirectMessagePageRoute } from "../direct-message/direct-message.page";
import { createUserWallPageRoute } from "../user-wall.page";
import AddToGroupModal from "./add-member-to-group-modal.component";
import InvitedUsersList from "./invited-users-list";

export const COMMUNITY_MEMBERS_BULK_ACTION_PAGE_ROUTE = "/settings/members";

// export const getMembersSettingsTabRelatedLink = (tabPath) =>
//   `/settings/members/${tabPath ?? ""}`;

const Tab = Object.freeze({
  members: "Members",
  invited: "Invited",
  adminMod: "Admin Mod",
});

function CommunityMembersBulkActionComponent({
  user,
  community,
  groups,
  onCommunityUpdate,
  addToast = (e) => {},
}) {
  const lang = useLang();
  const [selectedTab, setSelectedTab] = useState(Tab.members);
  const [isLoadingInvites, setIsLoadingInvites] = useState(false);
  const [members, setMembers] = useState([]);
  const [memberCount, setMembersCount] = useState(0);

  const [selectedMembers, setSelectedMembers] = useState([]);

  const [query, setQuery] = useState("");

  const [isUpdateRoleModalVisible, setIsUpdateRoleModalVisible] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [page, setPage] = useState(1);
  const [pageInviteIndex, setInvitePageIndex] = useState(1);

  // Contains the list paginated invited users
  const [invites, setInvites] = useState([]);

  /// Consist total no. of members. It is used to calculate total no. of pages
  const [totalInvites, setTotalInvites] = useState(0);

  const [isLoading, setIsLoading] = useState(false);

  const { cache, saveCache } = useCache();

  const communityId = community?.id;
  const userId = user?.id;

  useEffect(() => {
    if (community && user) {
      fetchInvites(pageInviteIndex, query);
    }
  }, [pageInviteIndex, communityId, userId]);

  // Get invites list
  function fetchInvites(page, query) {
    setIsLoadingInvites(true);
    EmailService.getInvitesList(user, community.id, query, page)
      .then(({ invites, total }) => {
        setInvites(invites);
        setInvitePageIndex(page);
        setTotalInvites(total);
        setIsLoadingInvites(false);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoadingInvites(false);
      });
  }

  const updateSubscriber = (subscriber) => {
    const newSubscribers = [...members];
    const memberIndex = newSubscribers.findIndex((m) => m.id === subscriber.id);
    newSubscribers.splice(memberIndex, 1, subscriber);
    setMembers(newSubscribers);
  };

  const markMembersAsViewed = (members) => {
    const userIds = members.filter((m) => m.isNew).map((m) => m.id);
    if (userIds.length > 0) {
      CommunityService.markSubscribersAsViewed(community.id, userIds);
      onCommunityUpdate({
        ...community,
        newUserCount: community.newUserCount - userIds.length,
      });
    }
  };

  // Get community members list
  useEffect(() => {
    if (userId && communityId) {
      if (cache[`members${page}`]) {
        setMembers(cache[`members${page}`]);
      }
      setIsLoading(true);
      CommunityService.getCommunitySubscribersPaginated(
        user,
        community.id,
        10,
        1,
        query
      )
        .then(({ users, total }) => {
          setMembers(users);
          setMembersCount(total);
          markMembersAsViewed(users);
          setIsLoading(false);
          if (query == "") {
            saveCache(`members${page}`, users);
          }
        })
        .catch((error) => {
          console.error({ error });
          setIsLoading(false);
        });
    }
  }, [communityId]);

  const onPageChange = (page) => {
    if (user && community.id) {
      if (cache[`members${page}`]) {
        setMembers(cache[`members${page}`]);
        setPage(page);
        return;
      }
      setIsLoading(true);
      CommunityService.getCommunitySubscribersPaginated(
        user,
        community.id,
        10,
        page,
        query
      )
        .then(({ users, total, page }) => {
          setMembers(users);
          setPage(page);
          setMembersCount(total);
          markMembersAsViewed(users);
          setIsLoading(false);
          if (query == "") {
            saveCache(`members${page}`, users);
          }
        })
        .catch((error) => {
          console.error({ error });
          setIsLoading(false);
        });
    }
  };

  const handleSearchDebounced = React.useCallback(
    debounce((query) => {
      if (["admin"].includes(community.myRole)) {
        setIsLoading(true);
        CommunityService.getCommunitySubscribersPaginated(
          user,
          community.id,
          10,
          1,
          query
        )
          .then(({ users, total }) => {
            setMembers(users);
            markMembersAsViewed(users);
            if (query === "") {
              saveCache(`members${page}`, users);
            }
            setMembersCount(total);
            setIsLoading(false);
          })
          .catch((error) => {
            console.error({ error });
            setIsLoading(false);
          });
      }
    }, 1000),
    [community.myRole]
  );

  const handleInviteSearchDebounced = React.useCallback(
    debounce((query) => {
      console.log(query);
      fetchInvites(1, query);
    }, 1000),
    [community]
  );

  return (
    <div className="Card rounded shadow theme-bg-surface">
      <div className="shadow">
        <div className="flex space-x-2  items-center w-1/2 px-4 pt-4">
          <div className="flex-none font-bold theme-text-heading-2">Member</div>
          <div className=" w-3/4">
            <TextInput
              key="1"
              prefix={<UIcon icon="search ml-2" />}
              noPrefixSeparator
              placeholder="Search"
              noBorder
              validationOutsideBox
              noMargin
              defaultValue={query}
              containerClassName=" theme-bg-surface py-1 focus:outline-none border theme-border-default rounded ml-2"
              onChange={(value) => {
                if (selectedTab === Tab.invited) {
                  handleInviteSearchDebounced(value);
                } else {
                  handleSearchDebounced(value);
                }
                setQuery(value);
              }}
              postfix={
                query.length > 0 ? (
                  <i
                    onClick={() => {
                      if (selectedTab === Tab.invited) {
                        handleInviteSearchDebounced("");
                      } else {
                        handleSearchDebounced("");
                      }
                      setQuery("");
                    }}
                    className="fi fi-rr-cross theme-text-heading-2 text-sm pr-1 cursor-pointer"
                  />
                ) : null
              }
            />
          </div>
        </div>
        <TabPills
          className="mt-6"
          selectedTab={selectedTab}
          setSelectedTab={(tabPath) => {
            setSelectedTab(tabPath);
            if (query !== "") {
              if (selectedTab === Tab.invited) {
                handleInviteSearchDebounced("");
              } else {
                handleSearchDebounced("");
              }
            }
            setQuery("");
          }}
          tabs={[
            {
              label: `${memberCount > 0 ? memberCount : ""} ${Tab.members}`,
              key: Tab.members,
            },
            {
              label: `${totalInvites > 0 ? totalInvites : ""} ${Tab.invited}`,
              key: Tab.invited,
            },
          ]}
        />
      </div>

      {/* <RenderSwitch /> */}
      <TabBody
        selectedTab={selectedTab}
        components={{
          Members: <MembersTab groups={groups} />,
          Invited: (
            <InvitedUsersList
              user={user}
              community={community}
              groups={groups}
              page={pageInviteIndex}
              setPage={setInvitePageIndex}
              invites={invites}
              setInvites={setInvites}
              totalInvites={totalInvites}
              setTotalInvites={setTotalInvites}
              isLoading={isLoadingInvites}
              addToast={addToast}
            />
          ),
        }}
      />
      <UpdateRoleModal
        selectedUser={isUpdateRoleModalVisible && selectedUser}
        // activeGroup={group}
        // updateGroupMember={updateGroupMember}
        updateSubscriber={updateSubscriber}
        unselectUser={(e) => {
          setIsUpdateRoleModalVisible(false);
          setSelectedUser(null);
        }}
      />
    </div>
  );

  function MembersTab({ groups }) {
    return (
      <div className="MembersTab mx-6 overflow-auto">
        <table className="table-auto min-w-full mb-2  h-full">
          <thead className="select-none">
            <tr>
              <th
                scope="col"
                className="flex mx-1 h-6 items-center place-content-center"></th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-bold theme-text-heading-1 uppercase tracking-wider">
                Members
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="theme-bg-surface p-2 border theme-border-default rounded-lg ">
            {isLoading
              ? [...Array(10)].map((_, index) => (
                  <MemberTableRowShimmer key={index} />
                ))
              : null}
            {!isLoading && members.length > 0 && selectedTab === Tab.members
              ? members.map((member, index) => (
                  <MemberTableRow
                    key={index}
                    member={member}
                    groups={groups}
                    isSelected={selectedMembers.includes(user)}
                    onSelected={(user) => {
                      if (
                        selectedMembers.some((member) => member.id === user.id)
                      ) {
                        setSelectedMembers(
                          selectedMembers.filter(
                            (member) => member.id !== user.id
                          )
                        );
                      } else {
                        setSelectedMembers([...selectedMembers, user]);
                      }
                    }}
                    selectedMembers={selectedMembers}
                  />
                ))
              : members.length > 0 && selectedTab === Tab.adminMod
              ? members.map((member, index) => {
                  if (member.communityRole !== "admin") {
                    return;
                  }
                  return (
                    <MemberTableRow
                      key={index}
                      groups={groups}
                      member={member}
                      isSelected={selectedMembers.includes(user)}
                      onSelected={(user) => {
                        if (
                          selectedMembers.some(
                            (member) => member.id === user.id
                          )
                        ) {
                          setSelectedMembers(
                            selectedMembers.filter(
                              (member) => member.id !== user.id
                            )
                          );
                        } else {
                          setSelectedMembers([...selectedMembers, user]);
                        }
                      }}
                      selectedMembers={selectedMembers}
                    />
                  );
                })
              : null}
          </tbody>
        </table>
        <div>
          <Pagination
            className="pagination-bar justify-center"
            currentPage={page}
            totalCount={memberCount}
            pageSize={10}
            onPageChange={(page) => onPageChange(page)}
          />
        </div>
      </div>
    );
  }

  function MemberTableRow({
    groups,
    member,
    isSelected,
    onSelected = (e) => {},
  }) {
    const [isAddToGroupVisible, setIsAddToGroupVisible] = useState(false);
    const [active, setActive] = useState(false);

    const removeSubscriber = (subscriber) => {
      const newMembers = [...members];
      const memberIndex = newMembers.findIndex((m) => m.id === subscriber.id);
      newMembers.splice(memberIndex, 1);
      setMembers(newMembers);
    };
    return (
      <tr className="group border-b theme-border-default hover:theme-bg-default">
        <td className="flex mx-1 mt-4 h-6 items-center place-content-center">
          {/* <Checkbox
            selected={isSelected}
            onClick={(val) => {
              onSelected(member);
            }}
          /> */}
        </td>
        <td className="px-2 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div className="flex items-center justify-center">
              <Avatar
                className="cursor-pointer"
                user={member}
                noName={true}
                onClick={(e) => {
                  // open the user page
                  window.open(createUserWallPageRoute(member.id, "activity"));
                }}
              />
            </div>
            <div className="flex items-center">
              <div className="ml-4">
                <div className="text-sm font-medium theme-text-heading-1">
                  {member.name}
                </div>
                <div className="text-xs theme-text-subtitle-1">
                  {member.email && member.mobile ? (
                    <span>
                      {" "}
                      {member.email}&nbsp; | &nbsp; {member.countryCode} &nbsp;
                      {member.mobile}{" "}
                    </span>
                  ) : member.email ? (
                    <span>{member.email}</span>
                  ) : member.mobile ? (
                    <span>
                      {member.countryCode}
                      &nbsp;
                      {member.mobile}
                    </span>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
              <div className="ml-2 mt-1 flex">
                {member.isNew ? (
                  <div className="bg-green-50 mx-2 font-semibold text-green-500 text-xxs px-1 rounded">
                    new
                  </div>
                ) : (
                  <></>
                )}
                {member.communityRole === "admin" ? (
                  <div className="bg-blue-50 mx-2 font-semibold text-blue-500 text-xxs px-1 rounded">
                    Community Admin
                  </div>
                ) : (
                  <></>
                )}
                {member.groupRole === "admin" ? (
                  <div className="bg-blue-50 mx-2 font-semibold text-blue-500 text-xxs px-1 rounded">
                    Admin
                  </div>
                ) : member.groupRole === "moderator" ? (
                  <div className="bg-green-50 mx-2 font-semibold text-green-500 text-xxs px-1 rounded">
                    Moderator
                  </div>
                ) : (
                  <></>
                )}
              </div>
              {/* show approval for user, when community needs verification */}
              {community.myRole === "admin" &&
              community.needsAdminVerification &&
              member.communityRole !== "admin" &&
              !member.communityAdminVerified ? (
                <div
                  className="bg-green-50 mx-2 font-semibold text-green-500 text-xxs px-2 py-1 border border-green-500 rounded cursor-pointer"
                  onClick={(e) => {
                    CommunityService.verifyUserByAdmin(
                      user,
                      community.id,
                      member.id
                    )
                      .then(() => {
                        updateSubscriber({
                          ...member,
                          communityAdminVerified: true,
                        });
                      })
                      .catch((error) => {
                        console.error({ error });
                      });
                  }}>
                  Accept
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </td>
        <td>
          <div>
            <div className="invisible group-hover:visible ">
              <IconMenu2
                dropdownClassName=" w-56 z-10 items-center "
                actions={[
                  {
                    icon: "plus",
                    label: "Add to group",
                    onClick: () => {
                      setIsAddToGroupVisible(true);
                    },
                  },
                  {
                    icon: "comment",
                    label: member.id !== user.id ? "Send Direct Message" : "",
                    onClick: (e) => {
                      e.preventDefault();
                      history.push(getDirectMessagePageRoute(member.id));
                    },
                  },
                  {
                    icon: "edit",
                    label: "Change Role",
                    onClick: () => {
                      setSelectedUser(member);
                      setIsUpdateRoleModalVisible(true);
                    },
                  },
                  {
                    // show make user admin modal
                    icon: "cross",
                    label: member.id !== user.id ? "Kick from community" : "",
                    onClick: (e) => {
                      // call the api to remove admin
                      setActive(true);
                    },
                    actionType: "alert",
                  },
                  // {
                  //   icon: "trash",
                  //   label: "Remove",
                  //   onClick: () => {},
                  //   actionType: "alert",
                  // },
                ]}
                onClick={(item) => {}}
              />
              <ActionModal
                active={active}
                setActive={setActive}
                header="Kick out from community"
                title={`Are you sure you want to remove ${member.name} ?`}
                onSubmit={(e) => {
                  CommunityService.removeUserFromCommunity(
                    user,
                    community.id,
                    member.id
                  )
                    .then((res) => {
                      // addToast('Role successfully updated', '', ToastTypes.success);
                      removeSubscriber(member);
                      addToast(
                        `${member.name} is removed from` + community.name,
                        "",
                        ToastTypes.success
                      );
                    })
                    .catch((err) => {
                      // addToast(err.message, '', ToastTypes.danger);
                      console.log("some error occurred", { err });
                      addToast(
                        "Error while removing the community member",
                        "",
                        ToastTypes.error
                      );
                    });
                }}
              />
            </div>

            <AddToGroupModal
              user={user}
              member={member}
              isActive={isAddToGroupVisible}
              setIsActive={setIsAddToGroupVisible}
              groups={groups}
              community={community}
            />
          </div>
        </td>
      </tr>
    );
  }

  function MemberTableRowShimmer() {
    return (
      <tr className="animate-pulse">
        <td className="flex h-16 m-auto items-center place-content-center  ">
          <div className="h-4 w-4 theme-bg-disable"></div>
        </td>
        <td className="px-4 py-4 ">
          <div className="flex items-center">
            <div className=" h-10 w-10">
              <p className="w-8 h-8 bg-gray-300 rounded-full "></p>
            </div>
            <div className="ml-4 space-y-3">
              <div className="w-20 h-2   bg-gray-300 rounded" />
              <div className="w-32 h-2  bg-gray-300 rounded" />
            </div>
          </div>
        </td>
        <td className=" px-4 py-4">
          <div className="w-6 h-6  bg-gray-200 rounded" />
        </td>
      </tr>
    );
  }
}

const Search = ({
  query = "",
  setQuery = () => {},
  handleSearchDebounced = () => {},
}) => {
  const [showInput, setShowInput] = useState(false);
  return (
    <div className="px-2 pt-4 mb-22 w-full theme-bg-surface">
      <div className="flex items-center w-3/4">
        <div className="flex-none font-bold theme-text-heading-2">Member</div>
        <div className="flex">
          {showInput ? (
            <TextInput
              key="1"
              prefix={<UIcon icon="search ml-2" />}
              noPrefixSeparator
              placeholder="Search by name"
              noBorder
              validationOutsideBox
              noMargin
              value={query}
              containerClassName="theme-bg-surface py-1 focus:outline-none border theme-border-default rounded ml-2"
              onChange={(value) => {
                // setIsLoading(true);
                setQuery(value);
                handleSearchDebounced(value);
              }}
              postfix={
                query.length > 0 ? (
                  <i
                    onClick={() => {
                      // setIsLoading(true);
                      setShowInput(!showInput);
                      setQuery("");
                      handleSearchDebounced("");
                    }}
                    className="fi fi-rr-cross theme-text-heading-2 text-sm pr-1 cursor-pointer"
                  />
                ) : null
              }
            />
          ) : (
            <TextInput
              key="2"
              prefix={<UIcon icon="search ml-2" />}
              noPrefixSeparator
              placeholder="Search by name"
              noBorder
              validationOutsideBox
              noMargin
              value={query}
              containerClassName="theme-bg-surface py-1 focus:outline-none border theme-border-default rounded ml-2"
              onChange={(value) => {
                // setIsLoading(true);
                setQuery(value);
                handleSearchDebounced(value);
              }}
              postfix={
                query.length > 0 ? (
                  <i
                    onClick={() => {
                      // setIsLoading(true);
                      setShowInput(!showInput);
                      setQuery("");
                      handleSearchDebounced("");
                    }}
                    className="fi fi-rr-cross theme-text-heading-2 text-sm pr-1 cursor-pointer"
                  />
                ) : null
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

const CommunityMembersBulkActionPage = withToast(
  connect(
    (s) => ({
      user: s.auth,
      community: s.community,
      groups: s.groups,
    }),
    (d) =>
      bindActionCreators(
        {
          onCommunityUpdate: setCommunity,
        },
        d
      )
  )(CommunityMembersBulkActionComponent)
);

export default CommunityMembersBulkActionPage;
